// ─────────────────────────────────────────────────────────────────────────────
// Respaldo interactivo para el SSO silencioso de Teams (`TeamsSDK.getSsoToken()`).
//
// Motivo: dentro de Teams, `microsoftTeams.authentication.getAuthToken()` es SIEMPRE silencioso —
// si la cuenta necesita un paso adicional (MFA/Conditional Access, o cookies de terceros bloqueadas
// por ITP en Safari/iOS), la llamada falla sin ninguna vía de recuperación (a diferencia de MSAL en
// navegador, que sí puede abrir un popup interactivo). `TeamsSDK.getSsoTokenInteractive()` cubre ese
// hueco reutilizando `microsoftTeams.authentication.authenticate()`, que Teams SÍ deja abrir una
// ventana interactiva.
//
// Esa ventana necesita cargar una URL con una Redirect URI ya registrada en el App Registration de
// Entra ID (a2d1655c, el mismo que usa MSAL — ver `msalConfig.ts`). Para NO depender de registrar
// una URI nueva (fuera del alcance de este cambio: requeriría acceso a Azure AD que no se tiene),
// la ventana reutiliza la RAÍZ de la app — la misma Redirect URI que MSAL ya usa en navegador — y se
// distingue de una carga normal con dos marcadores:
//   - `TEAMS_AUTH_POPUP_PARAM` en el query string: le dice a `AuthProvider` "arranca el login
//     interactivo de MSAL", en vez de su arranque normal. Solo importa en la carga INICIAL de la
//     ventana (antes de ir a Entra ID); Entra ID no lo conserva al redirigir de vuelta.
//   - `TEAMS_AUTH_POPUP_STATE`, pasado como `state` de MSAL: ESE sí sobrevive el round-trip a Entra
//     ID (es justo para eso que existe `state` en OAuth2), así que al volver `AuthProvider` puede
//     reconocer "esta carga es el regreso del popup de Teams" y notificarle el resultado a Teams en
//     vez de montar la app completa en la ventana emergente.
// ─────────────────────────────────────────────────────────────────────────────

export const TEAMS_AUTH_POPUP_PARAM = 'teamsAuthPopup';
export const TEAMS_AUTH_POPUP_STATE = 'teams-auth-popup';

/** URL de arranque del popup interactivo de Teams: la raíz de la app + el marcador de query. */
export function buildTeamsAuthPopupUrl(origin: string, basePath: string): string {
  return `${origin}${basePath}/?${TEAMS_AUTH_POPUP_PARAM}=1`;
}

/** `true` si la URL actual es la carga INICIAL del popup (antes de ir a Entra ID). */
export function isTeamsAuthPopupKickoff(search: string): boolean {
  return new URLSearchParams(search).has(TEAMS_AUTH_POPUP_PARAM);
}

/** `true` si el `state` que devolvió `handleRedirectPromise()` es el del popup de Teams. */
export function isTeamsAuthPopupReturn(state: string | null | undefined): boolean {
  return state === TEAMS_AUTH_POPUP_STATE;
}
